/**
 * hsg-state-map.js
 * Pick Your State — desktop SVG map + mobile available list
 *
 * Desktop:
 *   - Available states = lighter HSG red (#e87b83)
 *   - Tooltip follows cursor on pointer devices; shows on focus for keyboard/touch
 *   - Click or Enter/Space navigates straight to /pages/{state-handle}
 *
 * Mobile:
 *   - Alphabetical list of available states only
 *   - "More states coming soon" is a visible styled note (static HTML)
 *
 * Accessibility:
 *   - Available paths get tabindex="0", role="link", aria-label
 *   - Enter/Space triggers navigation
 */


(function () {
  'use strict';


  var STATE_HANDLES = {
    AL:'alabama', AK:'alaska', AZ:'arizona', AR:'arkansas', CA:'california',
    CO:'colorado', CT:'connecticut', DE:'delaware', DC:'district-of-columbia',
    FL:'florida', GA:'georgia', HI:'hawaii', ID:'idaho', IL:'illinois',
    IN:'indiana', IA:'iowa', KS:'kansas', KY:'kentucky', LA:'louisiana',
    ME:'maine', MD:'maryland', MA:'massachusetts', MI:'michigan', MN:'minnesota',
    MS:'mississippi', MO:'missouri', MT:'montana', NE:'nebraska', NV:'nevada',
    NH:'new-hampshire', NJ:'new-jersey', NM:'new-mexico', NY:'new-york',
    NC:'north-carolina', ND:'north-dakota', OH:'ohio', OK:'oklahoma', OR:'oregon',
    PA:'pennsylvania', RI:'rhode-island', SC:'south-carolina', SD:'south-dakota',
    TN:'tennessee', TX:'texas', UT:'utah', VT:'vermont', VA:'virginia',
    WA:'washington', WV:'west-virginia', WI:'wisconsin', WY:'wyoming'
  };


  function init() {
    var section = document.querySelector('.hsg-state-section');
    if (!section) return;


    var namesEl = section.querySelector('[data-state-names]');
    var stateNames = {};
    if (namesEl) {
      try { stateNames = JSON.parse(namesEl.textContent); } catch (e) {}
    }


    var mapEl = section.querySelector('[data-available]');
    var availableCodes = [];
    if (mapEl) {
      try { availableCodes = JSON.parse(mapEl.getAttribute('data-available')); } catch (e) {}
    }
    availableCodes = availableCodes.map(function (c) { return String(c).toUpperCase().trim(); });


    var mapContainer = section.querySelector('.hsg-state-section__map');
    if (mapContainer) {
      initDesktopMap(section, mapContainer, availableCodes, stateNames);
    }


    var mobileAvailable = section.querySelector('[data-mobile-available]');
    if (mobileAvailable) {
      buildMobileList(mobileAvailable, availableCodes, stateNames);
    }


    // Defensive: remove old mobile-coming list if a cached page version has it
    var mobileComing = section.querySelector('[data-mobile-coming]');
    if (mobileComing) {
      mobileComing.parentNode.removeChild(mobileComing);
      var comingHeading = section.querySelector('.hsg-state-section__group-heading--coming');
      if (comingHeading) comingHeading.parentNode.removeChild(comingHeading);
    }
  }


  function initDesktopMap(section, mapContainer, availableCodes, stateNames) {
    var tooltip = section.querySelector('[data-tooltip]');
    var isTouchDevice = window.matchMedia('(hover: none)').matches;


    // Mark available paths; add accessibility attributes
    mapContainer.querySelectorAll('[data-state]').forEach(function (path) {
      var code = String(path.getAttribute('data-state')).toUpperCase();
      var name = stateNames[code] || code;
      if (availableCodes.indexOf(code) !== -1) {
        path.classList.add('is-available');
        path.setAttribute('tabindex', '0');
        path.setAttribute('role', 'link');
        path.setAttribute('aria-label', 'Go to ' + name);
      }
    });


    // ── Tooltip helpers ──────────────────────────────────────────────────────────
    function showTooltip(text, x, y) {
      if (!tooltip) return;
      tooltip.textContent = text;
      tooltip.removeAttribute('hidden');
      // Keep tooltip within viewport
      var offset = 14;
      var tipW   = tooltip.offsetWidth || 80;
      var tipH   = tooltip.offsetHeight || 28;
      var left   = x + offset;
      var top    = y + offset;
      if (left + tipW > window.innerWidth - 10) left = x - tipW - offset;
      if (top  + tipH > window.innerHeight - 10) top  = y - tipH - offset;
      tooltip.style.left = left + 'px';
      tooltip.style.top  = top  + 'px';
    }


    function hideTooltip() {
      if (!tooltip) return;
      tooltip.setAttribute('hidden', '');
    }


    // ── Navigation ───────────────────────────────────────────────────────────────
    function goToState(code) {
      var handle = STATE_HANDLES[code] || code.toLowerCase();
      window.location.href = '/pages/' + handle;
    }


    // ── Mouse events (hover devices only) ───────────────────────────────────────
    if (!isTouchDevice) {
      mapContainer.addEventListener('mousemove', function (e) {
        var path = e.target.closest('[data-state].is-available');
        if (path) {
          var code = String(path.getAttribute('data-state')).toUpperCase();
          showTooltip(stateNames[code] || code, e.clientX, e.clientY);
        } else {
          hideTooltip();
        }
      });


      mapContainer.addEventListener('mouseleave', hideTooltip);
    }


    // ── Click ────────────────────────────────────────────────────────────────────
    mapContainer.addEventListener('click', function (e) {
      var path = e.target.closest('[data-state].is-available');
      if (!path) return;
      goToState(String(path.getAttribute('data-state')).toUpperCase());
    });


    // ── Keyboard ─────────────────────────────────────────────────────────────────
    mapContainer.addEventListener('keydown', function (e) {
      if (e.key !== 'Enter' && e.key !== ' ') return;
      var path = e.target.closest('[data-state].is-available');
      if (!path) return;
      e.preventDefault();
      goToState(String(path.getAttribute('data-state')).toUpperCase());
    });


    // Focus-based tooltip (keyboard + touch users)
    mapContainer.addEventListener('focusin', function (e) {
      var path = e.target.closest('[data-state].is-available');
      if (!path) return;
      var code = String(path.getAttribute('data-state')).toUpperCase();
      var rect = path.getBoundingClientRect();
      showTooltip(stateNames[code] || code, rect.left + rect.width / 2, rect.top);
    });


    mapContainer.addEventListener('focusout', function (e) {
      if (e.target.closest('[data-state]')) hideTooltip();
    });
  }


  // ── Mobile list ──────────────────────────────────────────────────────────────
  function buildMobileList(listEl, availableCodes, stateNames) {
    if (!availableCodes.length) return;


    var sorted = availableCodes.slice().sort(function (a, b) {
      return (stateNames[a] || a).localeCompare(stateNames[b] || b);
    });


    var fragment = document.createDocumentFragment();
    sorted.forEach(function (code) {
      var name   = stateNames[code] || code;
      var handle = STATE_HANDLES[code] || code.toLowerCase();
      var li = document.createElement('li');
      var a  = document.createElement('a');
      a.href        = '/pages/' + handle;
      a.textContent = name;
      li.appendChild(a);
      fragment.appendChild(li);
    });


    listEl.appendChild(fragment);
  }


  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }


})();